import { useEffect, useRef, useState } from 'react';
import './LandingPage.css';

const logo = process.env.PUBLIC_URL + '/simmer-logo-cropped.png';

// ── Scroll reveal hook ────────────────────────────────────────────────────────

function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add('revealed');
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

// ── Count-up widget ───────────────────────────────────────────────────────────

function CountUp({ target, decimals = 0 }) {
  const [val, setVal] = useState(0);
  const ref    = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || started.current) return;
        started.current = true;
        const duration = 1600;
        const fps      = 60;
        const steps    = (duration / 1000) * fps;
        let   step     = 0;

        const tick = () => {
          step++;
          // ease-out curve
          const progress = 1 - Math.pow(1 - step / steps, 3);
          setVal(target * progress);
          if (step < steps) requestAnimationFrame(tick);
          else setVal(target);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.4 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target]);

  const display = decimals > 0
    ? val.toFixed(decimals)
    : Math.floor(val).toLocaleString();

  return <span ref={ref}>{display}</span>;
}

// ── Landing page ──────────────────────────────────────────────────────────────

export default function LandingPage({ onGetStarted, onSignIn }) {
  useScrollReveal();

  return (
    <div className="lp">

      {/* ── Sticky nav ── */}
      <header className="lp-nav">
        <div className="lp-nav-inner">
          <span className="lp-nav-logo">
            Simmer
            <span className="lp-nav-subtitle">A CURATED PAUSE.</span>
          </span>
          <div className="lp-nav-actions">
            <button className="lp-nav-login" onClick={onSignIn}>Log in</button>
            <button className="lp-nav-cta"   onClick={onGetStarted}>Get started</button>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="lp-hero">
        <div className="lp-hero-inner">
          <div className="lp-hero-icon hero-fade-in">
            <img src={logo} alt="Simmer" className="lp-hero-logo" style={{ height: '120px', width: 'auto' }} />
          </div>
          <h1 className="lp-hero-headline hero-fade-in hero-delay-1">
            Let it simmer<br />before you buy it
          </h1>
          <p className="lp-hero-sub hero-fade-in hero-delay-2">
            Add something you want. Wait 3 days. Your wallet will thank you.
          </p>
          <p className="lp-hero-tagline hero-fade-in hero-delay-3">
            <em>impulse shopping is COOKED</em>
          </p>
          <div className="lp-hero-actions hero-fade-in hero-delay-4">
            <button className="lp-btn-primary" onClick={onGetStarted}>
              Start Simmering — it's free
            </button>
            <button className="lp-btn-ghost" onClick={onSignIn}>
              I already have an account
            </button>
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="lp-section lp-how">
        <div className="lp-section-inner">
          <h2 className="lp-section-heading reveal">How Simmer works</h2>
          <div className="lp-steps">
            <div className="lp-step reveal reveal-delay-1">
              <div className="lp-step-num">01</div>
              <h3 className="lp-step-title">Add it</h3>
              <p className="lp-step-body">
                Create a free account, then paste a product link into Simmer.
                The item lands in your list instantly.
              </p>
            </div>
            <div className="lp-step reveal reveal-delay-2">
              <div className="lp-step-num">02</div>
              <h3 className="lp-step-title">Let it simmer</h3>
              <p className="lp-step-body">
                We hold it for 3 days while the impulse fades. No reminders,
                no pressure — just a little time between the urge and the purchase.
              </p>
            </div>
            <div className="lp-step reveal reveal-delay-3">
              <div className="lp-step-num">03</div>
              <h3 className="lp-step-title">Decide with clarity</h3>
              <p className="lp-step-body">
                Still want it? Buy it intentionally. Changed your mind?
                Watch your savings grow.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why section ── */}
      <section className="lp-section lp-why">
        <div className="lp-section-inner">
          <h2 className="lp-section-heading reveal">
            We built Simmer because impulse buying
            <br className="lp-br-desktop" /> is designed into everything
          </h2>
          <div className="lp-why-body">
            <p className="reveal reveal-delay-1">
              Retailers, algorithms, and one-click buying are all engineered
              to make you act before you think. The "limited time offer."
              The empty cart that fills itself. The checkout flow that takes
              four seconds. None of it is accidental — it's all optimized
              to collapse the gap between wanting something and buying it.
            </p>
            <p className="reveal reveal-delay-2">
              Simmer puts a pause between the urge and the purchase. Not a
              lecture. Not a guilt trip. Just time. Three days is long enough
              for most impulse buys to dissolve on their own — and short
              enough that if you still want it, you were probably going to
              buy it anyway.
            </p>
          </div>
        </div>
      </section>

      {/* ── Savings dashboard preview ── */}
      <section className="lp-section lp-savings-section">
        <div className="lp-section-inner">
          <h2 className="lp-section-heading reveal">Watch your savings add up</h2>
          <p className="lp-section-sub reveal reveal-delay-1">
            Every time you walk away from an impulse purchase, Simmer logs it.
            At the end of each quarter you'll see exactly how much you kept
            in your pocket.
          </p>
          <div className="lp-dashboard-preview reveal reveal-delay-2">
            <div className="lp-dashboard-card">
              <p className="lp-dashboard-label">Saved this quarter</p>
              <p className="lp-dashboard-amount">
                $<CountUp target={847} />
              </p>
              <p className="lp-dashboard-items">
                <CountUp target={23} /> items walked away from
              </p>
            </div>
            <div className="lp-dashboard-secondary">
              <div className="lp-dash-stat">
                <span className="lp-dash-stat-val">$<CountUp target={3241} /></span>
                <span className="lp-dash-stat-label">All-time saved</span>
              </div>
              <div className="lp-dash-stat">
                <span className="lp-dash-stat-val"><CountUp target={74} />%</span>
                <span className="lp-dash-stat-label">Impulse control rate</span>
              </div>
              <div className="lp-dash-stat">
                <span className="lp-dash-stat-val"><CountUp target={89} /></span>
                <span className="lp-dash-stat-label">Items simmered</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Honest business model ── */}
      <section className="lp-section lp-biz">
        <div className="lp-section-inner lp-biz-inner">
          <h2 className="lp-section-heading reveal">
            How we make money
            <br />
            <span className="lp-biz-sub-head">(we'll be upfront)</span>
          </h2>
          <p className="lp-biz-body reveal reveal-delay-1">
            We only make money when you decide something was genuinely worth
            buying after waiting 3 days. When you click through from Simmer
            and purchase, we earn a small affiliate commission from the
            retailer — at no extra cost to you.
          </p>
          <p className="lp-biz-body reveal reveal-delay-2">
            We make nothing from impulse purchases you walk away from.
            That's a pretty different business model from every other
            shopping app out there.
          </p>
          <p className="lp-biz-body reveal reveal-delay-3">
            We will never sell your data, show you ads, or profit from you
            buying things you don't need. The whole business only works if
            Simmer actually helps you spend more intentionally.
          </p>
        </div>
      </section>

      {/* ── Footer CTA ── */}
      <section className="lp-footer-cta">
        <h2 className="lp-footer-cta-heading reveal">
          Ready to start simmering?
        </h2>
        <button className="lp-btn-primary reveal reveal-delay-1" onClick={onGetStarted}>
          Start Simmering — it's free
        </button>
      </section>

      {/* ── Footer ── */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <img src={logo} alt="Simmer" className="lp-footer-logo" />
            <span className="lp-footer-subtitle">A CURATED PAUSE.</span>
            <span className="lp-footer-tagline">
              <em>impulse shopping is COOKED</em>
            </span>
          </div>
          <nav className="lp-footer-links">
            <a href="#about"   className="lp-footer-link">About</a>
            <a href="#privacy" className="lp-footer-link">Privacy</a>
            <a href="#contact" className="lp-footer-link">Contact</a>
          </nav>
        </div>
      </footer>

    </div>
  );
}
